import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Grid, Inline, xcss } from '@atlaskit/primitives';
import Pressable from '@atlaskit/primitives/pressable';
import Text from '@atlaskit/primitives/text';

import { products } from '../../../../../../../common/constants/products';

import { messages } from './messages';

export interface OAuthMessageProps {
	onAuthorize: (e: React.MouseEvent<HTMLElement>) => void;
	onDismiss: (e: React.MouseEvent<HTMLElement>) => void;
	product: string;
	testId?: string;
}

const containerStyles = xcss({
	wordBreak: 'break-word',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.neutral',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const connectPressableStyles = xcss({
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	paddingInline: 'space.150',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	color: 'color.link',

	':hover': {
		textDecoration: 'underline',
		color: 'color.link.pressed',
	},
	':active': {
		textDecoration: 'underline',
		color: 'color.link.pressed',
	},
	whiteSpace: 'nowrap', // so that "Connect" doesn't get broken up needlessly
});

const actionsContainer = xcss({
	flexBasis: 'fit-content',
});

export const OAuthMessage = (props: OAuthMessageProps) => {
	const { product, onAuthorize, onDismiss, testId } = props;
	const { formatMessage } = useIntl();
	const productString = product === 'sharepoint' ? 'SharePoint' : products[product].displayName;
	const Icon = products[product].Icon;

	return (
		<Grid
			alignItems="center"
			templateAreas={['icon text cta']}
			templateColumns="min-content 1fr auto"
			columnGap="space.150"
			testId={testId}
			xcss={containerStyles}
		>
			<Icon appearance="brand" />
			<Inline space="space.050" alignBlock="center" shouldWrap>
				<Heading size="small">
					{product === 'figma' ? (
						<FormattedMessage
							{...messages.connectThirdPartyProductTitleAlternate}
							values={{ product: productString }}
						/>
					) : (
						<FormattedMessage
							{...messages.connectThirdPartyProductTitle}
							values={{ product: productString }}
						/>
					)}
				</Heading>
				<Text size="medium">
					<FormattedMessage {...messages.connectThirdPartyProductDescription} />
				</Text>
			</Inline>
			<Inline
				testId={testId && `${testId}--actions`}
				space="space.150"
				rowSpace="space.0"
				alignBlock="center"
				xcss={actionsContainer}
			>
				<Pressable
					onClick={onAuthorize}
					backgroundColor="color.background.neutral.subtle"
					xcss={connectPressableStyles}
				>
					<Text weight="medium" size="medium" color="color.link">
						<FormattedMessage {...messages.connect} />
					</Text>
				</Pressable>
				<IconButton
					icon={(iconProps) => <CrossIcon {...iconProps} label="" color="currentColor" />}
					onClick={onDismiss}
					label={formatMessage(messages.dismiss)}
					appearance={'subtle'}
					spacing={'compact'}
				/>
			</Inline>
		</Grid>
	);
};
